import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, CheckCircleIcon, XCircleIcon, InformationCircleIcon, CalendarIcon, MinusIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getInventoryLeftColor } from '../../components/InfoItem';
import { useDispatch, useSelector } from 'react-redux';
import { getShopifyAmazonSalesCalculation, SalesLoadingKeys } from '../../slices/inventorySlice';
import { ACTIVE, enumInventoryPhasedStatus, IN_ACTIVE, PHASED_STATUSES, SUNSET, PLANNED } from '../../consts/inventoryStatus';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { useNavigate } from 'react-router-dom';
import { Pagination } from '../../components/Pagination';
import { arrayHasItems, getEstimatedPODate, getYearsFromToCurrent, scrollToTop } from '../../helpers/common';
import { inventoryCalculationTableSettings, inventoryTableSettings, ROUTES, SORT_DIRECTION } from '../../consts/general';
import { OverlayGrayBackground } from '../../components/OverlayGrayBackground';
import { ALL, enumStockable, LESS_THEN_180, LOW_STOCK, NON_STOCKABLE, STOCKABLE, STOCKABLE_VALUES_ARRAY } from '../../consts/inventoryFilter';
import { useDebounce } from '../../helpers/useDebounce';
import { usePageReset } from '../../helpers/usePageReset';
import { getCronShopifyAmazonSalesStatus } from '../../slices/cronSlice';
import { CronUpdatesBlock } from '../../components/CronUpdatesBlock';
import { SHOPIFY_AMAZON_INVENTORY } from '../../consts/cronTypes';
import ReactTooltip from 'react-tooltip';
import FilterDropdown from '../../components/FilterDropdown';
import { SelectDropdown } from '../../components/SelectDropdown';
import { DatePickerInput } from '../../components/DatePickerInput';
import { fetchProductCatalogCategorySettings } from '../../slices/productCatalogSlice';
import ToggleButton from '../../components/ToggleButton';
import { calculateRoundedAverage, calculateSellOutDate, calculateStockDays, getReorderDeadline, getInventoryCalculationHeaderTooltip, calculateWarehouseStockDays, calculateWarehouseSellOutDate } from '../../helpers/inventoryUI';
import { format } from 'date-fns';
import { InventorySettings } from '../../components/inventory/InventorySettings';
import { BOOLEAN_FILTERS, enumCommonBooleanStatuses, NO, YES } from '../../consts/common';
import StockLevelsMobile, { MobilePaginationBar } from './StockLevelsMobile';
import axios from 'axios';
import { formatDateByGMT } from '../../helpers/formatDateByGMT';
import { getCronStatusColor } from '../../helpers/common';
import useStockLevelsAmazonFbaAccess from './useStockLevelsAmazonFbaAccess';

const CHANNEL_VEGAS = 'vegas';
const CHANNEL_AMAZON_FBA = 'amazon_fba';
const CHANNEL_OPTIONS = [
  { value: CHANNEL_VEGAS, label: 'Channel: Vegas warehouse' },
  { value: CHANNEL_AMAZON_FBA, label: 'Channel: Amazon FBA' },
];
const FBA_COLUMNS = [
  { label: 'FBA Sellable', value: 'fba_sellable', enableSorting: true },
  { label: 'FC Transfer', value: 'fba_fc_transfer', enableSorting: true },
  { label: 'Inbound', value: 'fba_inbound', enableSorting: true },
  { label: 'FBA Days', value: 'fba_days', enableSorting: true },
  { label: 'Send to FBA', value: 'fba_suggested_send', enableSorting: true },
];
const FBA_TOOLTIPS = {
  fba_sellable: 'Units Amazon can sell now (fulfillable).',
  fba_fc_transfer: 'Units Amazon is moving between its own fulfillment centers. They count as Amazon stock.',
  fba_inbound: 'Units on the way to Amazon: Amazon inbound, plus units Eadin removed for a transfer with no Amazon shipment yet. A shipment is never counted twice.',
  fba_days: '(FBA Sellable + FC Transfer + Inbound) / Amazon-only Daily Avg. Red: under the reorder point (lead time + safety days). Yellow: under the target cover.',
  fba_suggested_send: 'Units to reach the target cover, rounded up to the case pack and capped so Vegas keeps its own floor. Blank when the sync is not Fresh, the FNSKU needs mapping, or there are no Amazon sales.',
};
const FBA_SYNC_LABELS = { FRESH: 'Fresh', STALE: 'Stale', NO_DATA: 'No data' };
const FBA_SYNC_COLORS = { FRESH: getCronStatusColor('COMPLETED'), STALE: 'bg-yellow-500 text-white', NO_DATA: getCronStatusColor('ERROR') };
const getFbaDaysColor = (fba) => {
  if (!fba || fba.fbaDays === null || fba.fbaDays === undefined) return 'bg-gray-100 text-gray-800';
  if (fba.fbaDays < fba.fbaReorderDays) return 'bg-red-100 text-red-800';
  if (fba.fbaDays < fba.fbaTargetDays) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
};

const getInventoryStatusColor = (status) => {
  switch (status) {
    case ACTIVE:
      return 'bg-primary text-white';
    case IN_ACTIVE:
      return 'bg-red-100 text-red-800';
    case SUNSET:
      return 'bg-yellow-100 text-yellow-800'
    case PLANNED:
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-green-100 text-green-800';
  }
}

const InventoryByShopifyAndAmazonCalculation = ({
  testId,
  testData,
}) => {
  const navigate = useNavigate();
  const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
  const [currentPage, setCurrentPage] = useState(Number(pageParams?.get('page') || 1));
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(Number(pageParams?.get('itemsPerPage') || 50));
  const dispatch = useDispatch();
  const [activeFilter, setActiveFilter] = useState(pageParams?.get('filterBy') || ALL);
  const [searchTerm, setSearchTerm] = useState(pageParams?.get('search') || '');
  const [sortBy, setSortBy] = useState(pageParams?.get('sortBy') || 'product_line');
  const [sortDirection, setSortDirection] = useState(pageParams?.get('sortDirection') || 'ASC');
  const fbaAccess = useStockLevelsAmazonFbaAccess();
  const [channel, setChannel] = useState(pageParams?.get('channel') || CHANNEL_VEGAS);
  const isFba = channel === CHANNEL_AMAZON_FBA && fbaAccess.canView;
  const requestChannel = isFba ? CHANNEL_AMAZON_FBA : CHANNEL_VEGAS;
  const [fbaSends, setFbaSends] = useState({});
  const [fbaDraft, setFbaDraft] = useState(null);
  const amazonFba = useSelector((state) => state.inventory.amazonFba);
  const { 
    loading: cronLoading,
    data: cronData
  } = useSelector((state) => state.cron);
  const {
    shopifyAmazonSales: sales,
    loadingKeys: salesLoadingKeys,
    isFirstLoadComplete,
    loading,
    shopifyAmazonPagination: pagination,
    currentDemandForecastPlanningId,
    currentDemandForecastYear,
  } = useSelector((state) => state.inventory);
  const { categorySettingsItems } = useSelector((state) => state.productCatalog);

  const catalogCategoryOptionIds = useMemo(
    () => (categorySettingsItems || []).map((g) => String(g.id)),
    [categorySettingsItems]
  );
  const catalogCategoryNameById = useMemo(() => {
    const m = {};
    (categorySettingsItems || []).forEach((g) => {
      m[String(g.id)] = g.name;
    });
    return m;
  }, [categorySettingsItems]);
  const currentSearchValue = useDebounce(searchTerm, 800);
  
  // Auto-reset page to 1 when non-page params change
  const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, 'get:/api/v1/shopify-amazon-sales-calculation');
  
  const [editedExtraOrders, setEditedExtraOrders] = useState({});
  const tableContainerRef = useRef(null);
  const [selectedCategories, setSelectedCategories] = useState(pageParams?.get('categories')?.split(',') || []);
  const [selectedStockable, setSelectedStockable] = useState(pageParams?.get('showStockable')?.split(',') || [STOCKABLE, NON_STOCKABLE]);
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState(pageParams?.get('phasedStatuses')?.split(',') || [ACTIVE, SUNSET, PLANNED]);
  const [selectedIsNew, setSelectedIsNew] = useState(pageParams?.get('isNew')?.split(',') || [YES, NO]);
  const [selectedIsReplenishable, setSelectedIsReplenishable] = useState(pageParams?.get('isReplenishable')?.split(',') || [YES, NO]);
  const tableHeaderRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const stickyHeaderScrollRef = useRef(null);
  const isSyncingScrollRef = useRef(false);
  const [stickyHeaderWidths, setStickyHeaderWidths] = useState([]);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [stickyHeaderStyle, setStickyHeaderStyle] = useState({ top: 0, left: 0, width: 0 });
  const [stickyHeaderTableMinWidth, setStickyHeaderTableMinWidth] = useState(null);
  
  // Demand Basis state
  const [demandBasisType, setDemandBasisType] = useState(pageParams?.get('demandBasisType') || 'trailing'); // 'trailing' (triangle), 'custom', 'quarter'
  const [trailingPeriod, setTrailingPeriod] = useState(pageParams?.get('trailingPeriod') || '90D'); // '30D', '90D', '365D'
  const [customStartDate, setCustomStartDate] = useState(pageParams?.get('customStartDate') || '');
  const [customEndDate, setCustomEndDate] = useState(pageParams?.get('customEndDate') || '');
  const [customRangeApplied, setCustomRangeApplied] = useState(() => {
    // If custom type with dates in URL params, mark as applied
    const type = pageParams?.get('demandBasisType') || 'trailing';
    const startDate = pageParams?.get('customStartDate') || '';
    const endDate = pageParams?.get('customEndDate') || '';
    return type === 'custom' && startDate && endDate;
  });
  const [quarter, setQuarter] = useState(pageParams?.get('quarter') || 'Q1');
  const [year, setYear] = useState(pageParams?.get('year') || new Date().getFullYear().toString());
  const maxCurrentYearEndDate = `${new Date().getFullYear()}-12-31`;
  
  // Growth Multiplier state
  const [growthMultiplier, setGrowthMultiplier] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState('0');

  const currentIsFirstLoadComplete = testId && testData?.isFirstLoadComplete !== undefined ? testData?.isFirstLoadComplete : isFirstLoadComplete;
  const currentLoading = testId && testData?.loading !== undefined ? testData?.loading : loading;
  const currentLoadingKeys = testId && testData?.salesLoadingKeys !== undefined ? testData?.salesLoadingKeys : salesLoadingKeys;
  const currentSales = testId && testData?.sales !== undefined ? testData?.sales : sales;
  const fbaSendFor = (product) => {
    const suggested = product.fba?.fbaSuggestedSend;
    if (suggested === null || suggested === undefined) return null;
    return fbaSends[product.id] ?? suggested;
  };
  const fbaDraftLines = isFba
    ? (currentSales || []).map((product) => ({ sku: product.sku, quantity: fbaSendFor(product) || 0 })).filter((line) => line.quantity > 0)
    : [];
  const fbaDraftUnits = fbaDraftLines.reduce((sum, line) => sum + line.quantity, 0);
  const createFbaDraft = async () => {
    setFbaDraft({ busy: true, text: 'Creating draft...' });
    try {
      const { data } = await axios.post('/api/v1/stock-levels/amazon-fba/transfer-draft', { lines: fbaDraftLines });
      setFbaDraft({ number: data.transfer_number, text: `${data.transfer_number} created as a Draft. Review and publish it in Transfers.` });
    } catch (error) {
      const body = error.response?.data || {};
      setFbaDraft({ error: true, text: body.error || 'Could not create the FBA transfer draft' });
    }
  };

  const COLUMNS_STORAGE_KEY = 'inventory-calculation-columns-settings'
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(COLUMNS_STORAGE_KEY)
    return saved ? JSON.parse(saved) : {}
  });

  const filteredTableSettings = inventoryCalculationTableSettings
    .filter(column => !column.isOptional || visibleColumns[column.value])
    .flatMap(column => (isFba && column.value === 'warehouse_sell_out_date' ? [column, ...FBA_COLUMNS] : [column]));

  const scrollToTableTop = () => {
    if (testId) return
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const syncStickyHeaderWidths = () => {
    const thead = tableHeaderRef.current;
    if (!thead) return;
    const secondRowThs = thead.querySelectorAll('tr:nth-child(2) th');
    if (!secondRowThs?.length) return;
    const widths = Array.from(secondRowThs).map((th) => th.getBoundingClientRect().width);
    setStickyHeaderWidths(widths);

    // Use the actual scroll container width so the sticky header overflows exactly like the main table.
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const w = tableContainer.scrollWidth || 0;
      if (w > 0) setStickyHeaderTableMinWidth(w);
    }
  };

  const syncStickyHeaderVisibility = () => {
    const container = tableContainerRef.current;
    const thead = tableHeaderRef.current;
    if (!container || !thead) return;

    const rect = container.getBoundingClientRect();
    const headerHeight = thead.getBoundingClientRect().height || 0;
    const topOffset = 0;
    const shouldShow = rect.top < topOffset && rect.bottom > headerHeight + topOffset;
    setShowStickyHeader(shouldShow);

    if (shouldShow) {
      setStickyHeaderStyle({
        top: topOffset,
        left: Math.floor(rect.left),
        width: Math.floor(rect.width),
      });
    }
  };

  useEffect(() => {
    dispatch(fetchProductCatalogCategorySettings());
  }, [dispatch]);

  useEffect(() => {
    if (testId) return
    ReactTooltip.rebuild()
    dispatch(getCronShopifyAmazonSalesStatus({ type: SHOPIFY_AMAZON_INVENTORY }))

    const getMainScrollLeft = () => {
      const tableContainer = tableContainerRef.current;
      const tableWrapper = tableWrapperRef.current;
      if (tableContainer && tableContainer.scrollWidth > tableContainer.clientWidth) {
        return tableContainer.scrollLeft || 0;
      }
      return (tableWrapper?.scrollLeft || tableContainer?.scrollLeft || 0);
    };

    const setMainScrollLeft = (nextLeft) => {
      const tableContainer = tableContainerRef.current;
      const tableWrapper = tableWrapperRef.current;
      if (tableContainer) tableContainer.scrollLeft = nextLeft;
      if (tableWrapper) tableWrapper.scrollLeft = nextLeft;
    };

    let rafId = null;
    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncStickyHeaderWidths();
        syncStickyHeaderVisibility();

        // Always force-align overlay to the main table scrollLeft (no drift).
        const stickyScroller = stickyHeaderScrollRef.current;
        if (stickyScroller) {
          stickyScroller.scrollLeft = getMainScrollLeft();
        }
      });
    };

    const syncOverlayToMain = () => {
      const stickyScroller = stickyHeaderScrollRef.current;
      if (!stickyScroller) return;
      stickyScroller.scrollLeft = getMainScrollLeft();
    };

    const onMainScroll = () => {
      syncOverlayToMain();
    };

    // Make the header feel like it's part of the table: wheel/trackpad over the header
    // should scroll the main table, not the header itself.
    const onStickyWheel = (e) => {
      const dx = e.deltaX || 0;
      const dy = e.deltaY || 0;
      const horizontalDelta = e.shiftKey ? dy : dx;

      if (horizontalDelta) {
        setMainScrollLeft(getMainScrollLeft() + horizontalDelta);
        syncOverlayToMain();
        e.preventDefault();
      }
    };

    // Initial measure (next paint so widths are correct)
    scheduleUpdate();

    const tableContainer = tableContainerRef.current;
    const tableWrapper = tableWrapperRef.current;
    const stickyScroller = stickyHeaderScrollRef.current;

    if (tableContainer) tableContainer.addEventListener('scroll', onMainScroll, { passive: true });
    if (tableWrapper) tableWrapper.addEventListener('scroll', onMainScroll, { passive: true });
    // wheel must be non-passive to allow preventDefault
    if (stickyScroller) stickyScroller.addEventListener('wheel', onStickyWheel, { passive: false });
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (tableContainer) tableContainer.removeEventListener('scroll', onMainScroll);
      if (tableWrapper) tableWrapper.removeEventListener('scroll', onMainScroll);
      if (stickyScroller) stickyScroller.removeEventListener('wheel', onStickyWheel);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [])

  // Re-measure/re-sync after data/column changes so the sticky header stays scrollable + aligned.
  useEffect(() => {
    if (testId) return;
    requestAnimationFrame(() => {
      syncStickyHeaderWidths();
      syncStickyHeaderVisibility();
      const tableContainer = tableContainerRef.current;
      const tableWrapper = tableWrapperRef.current;
      const stickyScroller = stickyHeaderScrollRef.current;
      const mainScrollLeft =
        tableContainer && tableContainer.scrollWidth > tableContainer.clientWidth
          ? tableContainer.scrollLeft
          : (tableWrapper?.scrollLeft || tableContainer?.scrollLeft || 0);
      if (stickyScroller) {
        stickyScroller.scrollLeft = mainScrollLeft;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTableSettings.length, currentSales?.length, showStickyHeader]);

  useEffect(() => {
    if (testId) return
    scrollToTop();
  }, [currentPage, currentItemsPerPage]);

  const defaultInventoryRequest = () => {
    const salesData = {
      search: currentSearchValue,
      page: currentPage,
      itemsPerPage: currentItemsPerPage,
      activeFilter,
      isNew: selectedIsNew,
      sortDirection,
      sortBy,
      categories: selectedCategories,
      phasedStatuses: selectedPhasedStatuses,
      showStockable: selectedStockable,
      isReplenishable: selectedIsReplenishable,
      demandBasisType,
      trailingPeriod,
      customStartDate,
      customEndDate,
      quarter,
      year,
      channel: requestChannel
    };
    if (activeFilter === LOW_STOCK) {
      salesData.skipVelocity = true;
    }
    const canBeLoaded = 
      demandBasisType === 'current-demand' ||
      demandBasisType === 'trailing' || 
      (demandBasisType === 'custom' && customRangeApplied && customStartDate && customEndDate) ||
      (demandBasisType === 'quarter' && quarter && year);
    if (canBeLoaded) {
      dispatch(getShopifyAmazonSalesCalculation(salesData));
    }
    delete sales.activeFilter;
    const params = new URLSearchParams({ ...salesData, filterBy: activeFilter });
    navigate(`${ROUTES.SHOPIFY_AMAZON_INVENTORY}?${params}`, { replace: true });
  };

  useEffect(() => {
    if (testId) return
    
    if (currentPage === 1 && shouldSkipRequest()) {
      const salesData = {
        search: currentSearchValue,
        page: currentPage,
        itemsPerPage: currentItemsPerPage,
        activeFilter,
        isNew: selectedIsNew,
        sortDirection,
        sortBy,
        categories: selectedCategories,
        phasedStatuses: selectedPhasedStatuses,
        showStockable: selectedStockable,
        isReplenishable: selectedIsReplenishable,
        demandBasisType,
        trailingPeriod,
        customStartDate: customRangeApplied ? customStartDate : '',
        customEndDate: customRangeApplied ? customEndDate : '',
        quarter,
        year,
        channel: requestChannel
      };
      if (activeFilter === LOW_STOCK) {
        salesData.skipVelocity = true;
      }
      const params = new URLSearchParams({ ...salesData, filterBy: activeFilter });
      navigate(`${ROUTES.SHOPIFY_AMAZON_INVENTORY}?${params}`, { replace: true });
      return;
    }
    
    defaultInventoryRequest();
  }, [
    activeFilter,
    requestChannel,
    currentPage,
    currentItemsPerPage,
    currentSearchValue,
    sortBy,
    sortDirection,
    selectedCategories,
    selectedIsReplenishable,
    selectedPhasedStatuses,
    selectedStockable,
    selectedIsNew,
    demandBasisType,
    trailingPeriod,
    customRangeApplied,
    quarter,
    year
  ])
  
  // Reset customRangeApplied when switching away from custom type
  useEffect(() => {
    if (demandBasisType !== 'custom') {
      setCustomRangeApplied(false);
    }
  }, [demandBasisType])

  // Auto-apply custom range when switching to custom type if dates are available
  useEffect(() => {
    if (demandBasisType === 'custom' && customStartDate && customEndDate && customEndDate >= customStartDate) {
      setCustomRangeApplied(true);
    }
  }, [demandBasisType, customStartDate, customEndDate])

  const tableIsLoading = () => currentLoading && currentLoadingKeys.includes(SalesLoadingKeys.getShopifyAmazonSalesCalculation);

  const calculateGrowthAdjustedValues = (product) => {
    if (growthMultiplier <= 0) return null;
    
    const additionalOrder = editedExtraOrders[product.id]?.additionalOrder 
      ? Number(editedExtraOrders[product.id]?.additionalOrder || 0) 
      : 0;
    const onHand = Number(product?.onHand || 0);
    const onOrder = (Number(product?.onOrder) || 0) + additionalOrder;
    const baseSalesDailyAVG = Number(product?.salesDailyAVG || 0);

    if (!baseSalesDailyAVG || baseSalesDailyAVG <= 0) return null;
    
    const adjustedSalesDailyAVG = Number((baseSalesDailyAVG * (1 + growthMultiplier / 100)).toFixed(2));
    const adjustedDaysOfStock = Math.round(calculateStockDays(onHand || 0, onOrder || 0, adjustedSalesDailyAVG || 0));
    const adjustedWarehouseDaysOfStock = Math.round(calculateWarehouseStockDays(onHand || 0, adjustedSalesDailyAVG || 0));
    
    const adjustedReorderDeadline = getReorderDeadline(adjustedDaysOfStock || null);
    const adjustedSellOutDate = calculateSellOutDate(Number(adjustedDaysOfStock || 0));
    const adjustedWarehouseSellOutDate = calculateWarehouseSellOutDate(onHand, adjustedWarehouseDaysOfStock, adjustedSalesDailyAVG);
    
    const growthFactor = 1 + growthMultiplier / 100;

    return {
      adjustedSalesDailyAVG,
      adjustedSalesLast30: Math.round(Number(product?.salesLast30 || 0) * growthFactor),
      adjustedSalesLast60: Math.round(Number(product?.salesLast60 || 0) * growthFactor),
      adjustedSalesLast90: Math.round(Number(product?.salesLast90 || 0) * growthFactor),
      adjustedSalesLast365: Math.round(Number(product?.salesLast365 || 0) * growthFactor),
      daysOfStock: adjustedDaysOfStock || 0,
      warehouseDaysOfStock: adjustedWarehouseDaysOfStock || 0,
      sellOut180Days: adjustedDaysOfStock === null ? null : adjustedDaysOfStock < 181,
      sellOutDate: adjustedSellOutDate,
      warehouseSellOutDate: adjustedWarehouseSellOutDate,
      reorderDeadline: adjustedReorderDeadline ? format(new Date(adjustedReorderDeadline), 'MMM d, yyyy') : null,
    };
  };

  const calculateRelatedByAdditionalOrder = (product) => {
    if (editedExtraOrders[product.id]?.additionalOrder) {
      const additionalOrder = Number(editedExtraOrders[product?.id]?.additionalOrder || 0);
      const onHand = Number(product?.onHand || 0);
      const onOrder = (Number(product?.onOrder) || 0) + additionalOrder;
      const averageDailySales = Number(product?.salesDailyAVG || 0);
      
      const stockDays = Math.round(calculateStockDays(onHand || 0, onOrder || 0, averageDailySales || 0));
      const reorderDeadline = getReorderDeadline(stockDays || null);
      return {
        daysOfStock: stockDays || 0,
        sellOut180Days: stockDays === null ? null : stockDays < 181,
        sellOutDate: calculateSellOutDate(stockDays),
        reorderDeadline: reorderDeadline ? format(new Date(reorderDeadline), 'MMM d, yyyy') : null,
        // onOrder
      }
    }
    return null
  }

  const getCategoryAndColor = (product) => {
    if (!product) return null;
    if (!product.color && !product.category) return null;
    if (product.color && product.category) return `${product.color} | ${product.category}`;
    if (!product.color && product.category) return product.category;
    if (product.color && !product.category) return product.color;
    return null;
  }

  const onTableHeaderClick = (header, enableSorting) => {
    if (!enableSorting) return;

    setCurrentPage(1);
    if (sortBy === header) {
      setSortDirection((prev) => (prev === SORT_DIRECTION.ASCENDING ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING));
    } else {
      setSortBy(header);
      setSortDirection(SORT_DIRECTION.ASCENDING);
    }
  };

  const getSortClass = (column) => {
    if (sortBy !== column) return "";
    return sortDirection === SORT_DIRECTION.ASCENDING ? "triangle-up" : "triangle-down";
  };

  const getArrivesOnResult = (arrivesOn, sku, line, arrivesOnValue, productLeadTime) => {
    const leadTimeDays = Number(productLeadTime || 0);
    const renderTooltip = () => {
      return (
        <>
          <InformationCircleIcon className='h-5 w-5 text-primary' data-tip={true} data-for={`${sku}_${line}`} />
          <ReactTooltip
            id={`${sku}_${line}`}
            html={true}
            effect='solid'
          >
            {Object.entries(arrivesOn).map(([key, value]) => (
              `<div key={${key}} className='leading-7'>
                <span className="font-medium">${key}:</span> ${getEstimatedPODate(value, leadTimeDays)}
              </div>`
            )).join('')}
          </ReactTooltip>
        </>
      )
    }
    if (arrivesOn && Object.keys(arrivesOn).length > 0) {
      return (
        <div className='relative flex  justify-center gap-2 flex-row'>
          {arrivesOnValue} {Object.keys(arrivesOn).length > 0 && (renderTooltip())}
        </div>
      )
    }
    return "Nothing On Order"
  }

  const getPartsOfKitTooltip = (partsOfKit, tooltipId) => {
    return (
      <>
        <InformationCircleIcon className='h-5 w-5 text-primary' data-tip={true} data-for={tooltipId} />
        <ReactTooltip id={tooltipId} effect="solid" place="right">
          <div>
            <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th className="text-left">
                    SKU
                  </th>
                  <th className="text-right">
                    Qty
                  </th>
                </tr>
              </thead>
              <tbody>
                {partsOfKit.map((part) => (
                  <tr key={part.sku}>
                    <td className="text-left !bg-transparent">
                      {part.sku}
                    </td>
                    <td className="text-right">
                      {part.quantity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ReactTooltip>
      </>
    )
  }
  
  const getOnOrderDetailsTooltip = (onOrderDetails, sku) => {
    if (!onOrderDetails || !arrayHasItems(onOrderDetails.pos)) return null;
    const tooltipId = `onorder_${sku}`;
    const tooltipHtml = onOrderDetails.pos.map((po) => {
      const poNum = po?.po_number || 'N/A';
      const poCount = po?.po_count != null ? po.po_count : '0';
      return `<div key=${po.po_number} class='leading-6'>
        <span class="font-medium">${poNum}:</span> ${poCount}
      </div>`;
    }).join('') || 'N/A';
    return (
      <>
        <InformationCircleIcon
          className="h-5 w-5 text-primary"
          data-tip={true}
          data-for={tooltipId}
        />
        <ReactTooltip id={tooltipId} html={true} effect="solid" place="right">
          {tooltipHtml}
        </ReactTooltip>
      </>
    );
  }
  
  const getPageTitle = () => {
    return "Stock Levels"
  }

  const getMobileTotalLabel = () => {
    const match = /of\s+([\d,]+)/i.exec(pagination?.rangeRows || '');
    if (match) return `${match[1]} products`;
    return `${currentSales.length} products`;
  }

  const skipDetailsTooltip = (column) => {
    return column === "status" || column === "product_line";
  }

  const renderHeaderContent = ({ disableTooltips = false, applyWidths = false, tooltipIdSuffix = '' } = {}) => {
    return (
      <>
        <tr className="bg-white shadow-sm">
          <th colSpan="1" className="text-left after:!content-none"></th>
          <th colSpan="4" className="border border-blue-200 bg-blue-50 text-blue-900 text-center px-4 py-2 text-xs font-semibold uppercase">
            Warehouse stock
          </th>
          {isFba && (
            <th colSpan={FBA_COLUMNS.length} className="border border-orange-200 bg-orange-50 text-orange-900 text-center px-4 py-2 text-xs font-semibold uppercase">
              Amazon FBA
            </th>
          )}
          {(() => {
            const salesColumns = ['sales_last_30', 'sales_last_90', 'sales_last_365', 'sales_daily_avg'];
            const visibleSalesColumns = salesColumns.filter(col =>
              filteredTableSettings.some(setting => setting.value === col)
            );
            return visibleSalesColumns.length > 0 ? (
              <th colSpan={visibleSalesColumns.length} className="border border-purple-200 bg-purple-50 text-purple-900 text-center px-4 py-2 text-xs font-semibold uppercase">
                Sales
              </th>
            ) : null;
          })()}
          {(() => {
            const stockColumns = [ 'extra_ordered','days_of_stock','sell_out_date','reorder_deadline', 'reorder_quantity', 'stock_arrives_on'];
            const visibleStockColumns = stockColumns.filter(col =>
              filteredTableSettings.some(setting => setting.value === col)
            );
            return visibleStockColumns.length > 0 ? (
              <th colSpan={visibleStockColumns.length} className="border border-emerald-200 bg-emerald-50 text-emerald-900 text-center px-4 py-2 text-xs font-semibold uppercase">
                ON ORDER STOCK
              </th>
            ) : null;
          })()}
        </tr>
        <tr className="bg-gray-50 shadow-sm">
          {filteredTableSettings.map((header, index) => (
            <th
              key={header.value}
              onClick={() => onTableHeaderClick(header.value, header.enableSorting)}
              className={`${header.enableSorting && 'cursor-pointer'} px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider ${
                header.value === 'product_line'
                  ? 'text-left w-[300px] min-w-[300px] max-w-[300px]'
                  : 'text-center'
              }`}
              style={
                applyWidths && stickyHeaderWidths[index] != null
                  ? {
                      width: `${stickyHeaderWidths[index]}px`,
                      minWidth: `${stickyHeaderWidths[index]}px`,
                      boxSizing: 'border-box',
                    }
                  : undefined
              }
            >
              <div className={`flex items-center gap-2 ${header.value === 'product_line' ? '' : 'justify-center'}`}>
                {header.value === 'product_line' ? (
                  <div className="flex flex-row gap-2 items-center justify-between flex-1">
                    {header.label}
                    <div className="flex flex-row gap-2 items-center justify-between">
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <InventorySettings 
                          onVisibilityChange={setVisibleColumns} 
                          STORAGE_KEY={COLUMNS_STORAGE_KEY}
                        />
                      </div>
                      {header.enableSorting && (
                        <span className={getSortClass(header.value)}></span>
                      )}
                    </div>
                  </div>
                ) : (
                  header.label
                )}
                {!disableTooltips && !skipDetailsTooltip(header.value) && (
                  <>
                    <InformationCircleIcon
                      className="h-5 w-5 min-w-5 min-h-5 flex-shrink-0 text-primary"
                      data-tip={true}
                      data-for={`${header.value}_tooltip${tooltipIdSuffix ? `_${tooltipIdSuffix}` : ''}`}
                    />
                    <ReactTooltip
                      id={`${header.value}_tooltip${tooltipIdSuffix ? `_${tooltipIdSuffix}` : ''}`}
                      html={true}
                      effect="solid"
                      place="left"
                      className="text-xs normal-case"
                    >
                      {FBA_TOOLTIPS[header.value] || getInventoryCalculationHeaderTooltip(header.value) || header.value}
                    </ReactTooltip>
                  </>
                )}
                {header.enableSorting && header.value !== 'product_line' && (
                  <span className={getSortClass(header.value)}></span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </>
    );
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid={testId}>
      <div className="hidden md:flex flex-wrap mb-2 justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">{getPageTitle()}</h1>
        <div className="flex flex-wrap gap-2">
          <CronUpdatesBlock
              type={SHOPIFY_AMAZON_INVENTORY}
              status={cronData[SHOPIFY_AMAZON_INVENTORY]?.process_status}
              updatedDate={cronData[SHOPIFY_AMAZON_INVENTORY]?.last_update}
              errorText={cronData[SHOPIFY_AMAZON_INVENTORY]?.process_error}
              isLoading={cronLoading}
          />
          {isFba && amazonFba?.sync && (
            <section className="bg-white p-2 rounded-lg shadow w-auto max-w-80 flex" data-testid="amazon-fba-sync-status">
              <div className="flex gap-2 text-base items-center justify-center">
                <h6 className="font-medium">Amazon FBA sync:</h6>
                <div className={`px-3 text-xs py-0.5 rounded-xl ${FBA_SYNC_COLORS[amazonFba.sync.state] || FBA_SYNC_COLORS.NO_DATA}`}>
                  {FBA_SYNC_LABELS[amazonFba.sync.state] || amazonFba.sync.state}
                </div>
                <ReactTooltip id="amazon_fba_sync_tooltip" html={true} effect="solid">
                  {`<div class="w-72"><div>Updated: ${amazonFba.sync.finished_at ? formatDateByGMT(amazonFba.sync.finished_at) : 'N/A'}</div>${amazonFba.sync.state === 'FRESH' ? '' : '<div>Send to FBA is locked until the sync is Fresh.</div>'}${amazonFba.sync.consecutive_failures ? `<div>Failed runs in a row: ${amazonFba.sync.consecutive_failures}</div>` : ''}</div>`}
                </ReactTooltip>
                <InformationCircleIcon className="h-5 w-5 text-black-600 hover:text-black-900" data-for="amazon_fba_sync_tooltip" data-tip={true} />
              </div>
            </section>
          )}
        </div>
      </div>
      <div className="w-full">
        {/* Main content */}
        <div className="py-6">

          {/* Inventory Forecast Summary */}
          <div className="relative ">
            {currentIsFirstLoadComplete && tableIsLoading() && <OverlayGrayBackground testId={testId ? `overlay-loading-${testId}` : undefined} />}
            <StockLevelsMobile
              products={currentSales}
              totalLabel={getMobileTotalLabel()}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
              selectedPhasedStatuses={selectedPhasedStatuses}
              setSelectedPhasedStatuses={setSelectedPhasedStatuses}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              catalogCategoryOptionIds={catalogCategoryOptionIds}
              catalogCategoryNameById={catalogCategoryNameById}
              selectedIsNew={selectedIsNew}
              setSelectedIsNew={setSelectedIsNew}
              selectedIsReplenishable={selectedIsReplenishable}
              setSelectedIsReplenishable={setSelectedIsReplenishable}
              selectedStockable={selectedStockable}
              setSelectedStockable={setSelectedStockable}
              demandBasisType={demandBasisType}
              setDemandBasisType={setDemandBasisType}
              trailingPeriod={trailingPeriod}
              setTrailingPeriod={setTrailingPeriod}
              customStartDate={customStartDate}
              setCustomStartDate={setCustomStartDate}
              customEndDate={customEndDate}
              setCustomEndDate={setCustomEndDate}
              setCustomRangeApplied={setCustomRangeApplied}
              quarter={quarter}
              setQuarter={setQuarter}
              year={year}
              setYear={setYear}
              growthMultiplier={growthMultiplier}
              setGrowthMultiplier={setGrowthMultiplier}
              setSelectedPreset={setSelectedPreset}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              setCurrentPage={setCurrentPage}
              editedExtraOrders={editedExtraOrders}
              setEditedExtraOrders={setEditedExtraOrders}
              calculateGrowthAdjustedValues={calculateGrowthAdjustedValues}
              calculateRelatedByAdditionalOrder={calculateRelatedByAdditionalOrder}
              getCategoryAndColor={getCategoryAndColor}
              cronStatus={cronData[SHOPIFY_AMAZON_INVENTORY]?.process_status}
              cronUpdatedDate={cronData[SHOPIFY_AMAZON_INVENTORY]?.last_update}
              cronError={cronData[SHOPIFY_AMAZON_INVENTORY]?.process_error}
              cronLoading={cronLoading}
            />
            <div className="p-6 bg-white rounded-lg shadow-md border border-gray-300 hidden md:flex flex-col gap-4">
              <div className="flex flex-wrap justify-between items-start sm:items-center gap-2">
                  <div className="flex flex-wrap gap-2 space-y-2 sm:space-y-0 sm:space-x-4">
                    <div className="flex flex-wrap gap-4">
                      <div className="relative">
                        <FilterDropdown
                          options={BOOLEAN_FILTERS}
                          selectedOptions={selectedIsNew}
                          activeCalculation={true}
                          onChange={setSelectedIsNew}
                          placeholder="Is New"
                          renderOption={(status) => (
                            <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
                          )}
                          testId={testId ? `${testId}_is_new_filter` : undefined}
                        />
                      </div>
                      <div className="relative">
                        <FilterDropdown
                          options={PHASED_STATUSES.filter((status) => status !== IN_ACTIVE)}
                          selectedOptions={selectedPhasedStatuses}
                          activeCalculation={true}
                          onChange={setSelectedPhasedStatuses}
                          placeholder="Status"
                          renderOption={(phase) => (
                            <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
                          )}
                          testId={testId ? `${testId}_phased_filter` : undefined}
                        />
                      </div>
                      <div className="relative">
                        <FilterDropdown
                          options={BOOLEAN_FILTERS}
                          selectedOptions={selectedIsReplenishable}
                          activeCalculation={true}
                          onChange={setSelectedIsReplenishable}
                          placeholder="Replenishable"
                          renderOption={(replenishable) => (
                            <span className="text-gray-900">{enumCommonBooleanStatuses[replenishable]}</span>
                          )}
                          testId={testId ? `${testId}_replenishable_filter` : undefined}
                        />
                      </div>
                      <div className="relative">
                        <FilterDropdown
                          options={catalogCategoryOptionIds}
                          selectedOptions={selectedCategories}
                          activeCalculation={true}
                          onChange={setSelectedCategories}
                          placeholder="Categories"
                          renderOption={(categoryId) => (
                            <span className="text-gray-900">
                              {catalogCategoryNameById[categoryId] || categoryId}
                            </span>
                          )}
                          testId={testId ? `${testId}_category_filter` : undefined}
                        />
                      </div>
                      <div className="relative">
                        <FilterDropdown
                          options={STOCKABLE_VALUES_ARRAY}
                          selectedOptions={selectedStockable}
                          activeCalculation={true}
                          onChange={setSelectedStockable}
                          placeholder="Stockable"
                          renderOption={(stockable) => (
                            <span className="text-gray-900">{enumStockable[stockable]}</span>
                          )}
                          testId={testId ? `${testId}_stockable_filter` : undefined}
                        />
                      </div>
                      <ToggleButton
                        isActive={activeFilter === LOW_STOCK}
                        onToggle={() => setActiveFilter(activeFilter === LOW_STOCK ? ALL : LOW_STOCK)}
                        activeText="Low Stock Alerts"
                        activeCalculation={true}
                        inactiveText="Low Stock Alerts"
                        ToggleIcon={ExclamationTriangleIcon}
                        testId={testId ? `${testId}_low_stock_toggle` : undefined}
                      />
                      <ToggleButton
                        isActive={activeFilter === LESS_THEN_180}
                        onToggle={() => setActiveFilter(activeFilter === LESS_THEN_180 ? ALL : LESS_THEN_180)}
                        activeText="<180d inventory"
                        activeCalculation={true}
                        inactiveText="<180d inventory"
                        ToggleIcon={CalendarIcon}
                        testId={testId ? `${testId}_180d_toggle` : undefined}
                      />
                      <ToggleButton
                        isActive={activeFilter === ALL}
                        onToggle={() => setActiveFilter(ALL)}
                        activeText="All"
                        activeCalculation={true}
                        inactiveText="All"
                        testId={testId ? `${testId}_all_toggle` : undefined}
                      />
                    </div>
                  </div>
              </div>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex flex-wrap gap-4 justify-between items-center flex-1">
                  <div className="flex flex-wrap gap-4 flex-1">
                    {fbaAccess.canView && (
                      <div className="relative min-w-[240px]">
                        <SelectDropdown
                          options={CHANNEL_OPTIONS}
                          value={channel}
                          onChange={(value) => { setChannel(value); setFbaSends({}); setFbaDraft(null); setCurrentPage(1); }}
                          placeholder="Channel"
                          testId={testId ? `${testId}_channel_filter` : 'stock-levels-channel-filter'}
                        />
                      </div>
                    )}
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 min-h-[260px] bg-white rounded-lg shadow-md my-4 border border-gray-200 hidden md:block">
              <section className="flex flex-row gap-6 items-start">
                <div className="flex flex-col gap-4 flex-1">
                  <div className="flex flex-row gap-4 items-center">
                      <h3 className="text-lg font-bold">Demand Basis</h3>
                      <InformationCircleIcon className="h-5 w-5 text-primary" data-tip={true} data-for="demand_basis_tooltip" />
                      <ReactTooltip id="demand_basis_tooltip" html={true} effect="solid" place="right">
                        Changes what sales history window is used to compute 30D/90D/365D sales, Daily Avg, Days of Stock, and Sellout Date
                      </ReactTooltip>
                      {isFba && (
                        <span className="text-xs font-medium text-orange-900 bg-orange-50 border border-orange-200 rounded px-2 py-0.5" data-testid="amazon-fba-demand-note">
                          Amazon FBA: FBA Days and Send to FBA use Amazon-only net sales
                        </span>
                      )}
                    </div>
                    {/* Type Selector Tabs */}
                    <div className="flex flex-row gap-2 flex-1 bg-gray-100 rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => setDemandBasisType('trailing')}
                        className={`px-4 py-2 flex-1 rounded-lg text-sm font-medium transition-colors ${
                          demandBasisType === 'trailing'
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Trailing Range
                      </button>
                      <button
                        type="button"
                        onClick={() => setDemandBasisType('custom')}
                        className={`px-4 py-2 flex-1 rounded-lg text-sm font-medium transition-colors ${
                          demandBasisType === 'custom'
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Custom Range
                      </button>
                      <button
                        type="button"
                        onClick={() => setDemandBasisType('quarter')}
                        className={`px-4 py-2 flex-1 rounded-lg text-sm font-medium transition-colors ${
                          demandBasisType === 'quarter'
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        YoY Quarter
                      </button>
                      <button
                        type="button"
                        onClick={() => setDemandBasisType('current-demand')}
                        className={`px-4 py-2 flex-1 rounded-lg text-sm font-medium transition-colors ${
                          demandBasisType === 'current-demand'
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Current Demand
                      </button>
                    </div>
                    {/* Trailing Range View */}
                    {demandBasisType === 'trailing' && (
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-row gap-2">
                          <button
                            type="button"
                            onClick={() => setTrailingPeriod('30D')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              trailingPeriod === '30D'
                                ? 'bg-primary text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Last 30D
                          </button>
                          <button
                            type="button"
                            onClick={() => setTrailingPeriod('90D')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              trailingPeriod === '90D'
                                ? 'bg-primary text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Last 90D
                          </button>
                          <button
                            type="button"
                            onClick={() => setTrailingPeriod('365D')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              trailingPeriod === '365D'
                                ? 'bg-primary text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Last 365D
                          </button>
                        </div>
                        <p className="text-sm text-gray-500">
                          Uses trailing sales window to compute Daily Avg + Sellout.
                        </p>
                      </div>
                    )}
                    {/* Custom Range View */}
                    {demandBasisType === 'custom' && (
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-row gap-4 items-end">
                          <div className="flex flex-col gap-1 flex-1">
                            <label className="text-sm font-medium text-gray-700">Start Date</label>
                            <DatePickerInput
                              value={customStartDate}
                              onChange={(newStartDate) => {
                                setCustomStartDate(newStartDate);
                                // If end date is before new start date, clear it
                                if (customEndDate && newStartDate && customEndDate < newStartDate) {
                                  setCustomEndDate('');
                                }
                              }}
                              placeholder="Start date"
                              min="2023-01-01"
                              max={maxCurrentYearEndDate}
                              className="w-full"
                              buttonClassName="!py-2"
                            />
                          </div>
                          <div className="flex flex-col gap-1 flex-1">
                            <label className="text-sm font-medium text-gray-700">End Date</label>
                            <DatePickerInput
                              value={customEndDate}
                              onChange={setCustomEndDate}
                              placeholder="End date"
                              min={customStartDate || undefined}
                              max={maxCurrentYearEndDate}
                              className="w-full"
                              buttonClassName={`!py-2 ${
                                customStartDate && customEndDate && customEndDate < customStartDate
                                  ? '!border-red-500'
                                  : ''
                              }`}
                            />
                          </div>
                        </div>
                        {customStartDate && customEndDate && customEndDate < customStartDate && (
                          <p className="text-sm text-red-600">
                            End date cannot be before start date.
                          </p>
                        )}
                        <div className="flex flex-row gap-2 items-center">
                          <button
                            type="button"
                            onClick={() => {
                              if (customStartDate && customEndDate) {
                                // Only apply if dates are valid
                                if (customEndDate >= customStartDate) {
                                  setCustomRangeApplied(true);
                                  // Trigger request immediately
                                  defaultInventoryRequest();
                                }
                              }
                            }}
                            disabled={!customStartDate || !customEndDate}
                            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/80 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCustomStartDate('');
                              setCustomEndDate('');
                              setCustomRangeApplied(false);
                            }}
                            className="px-4 py-2 text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors"
                          >
                            Reset
                          </button>
                        </div>
                        <p className="text-sm text-gray-500">
                          Use any period (e.g., Q1 only) to avoid Q4 inflation.
                        </p>
                      </div>
                    )}
                    {/* YoY Quarter View */}
                    {demandBasisType === 'quarter' && (
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-row gap-4 items-end">
                          <div className="flex flex-col gap-1 flex-1">
                            <label className="text-sm font-medium text-gray-700">Quarter</label>
                            <SelectDropdown
                              options={[
                                { value: 'Q1', label: 'Q1' },
                                { value: 'Q2', label: 'Q2' },
                                { value: 'Q3', label: 'Q3' },
                                { value: 'Q4', label: 'Q4' }
                              ]}
                              value={quarter}
                              onChange={setQuarter}
                              placeholder="Select Quarter"
                            />
                          </div>
                          <div className="flex flex-col gap-1 flex-1">
                            <label className="text-sm font-medium text-gray-700">Year</label>
                            <SelectDropdown
                              options={getYearsFromToCurrent(2024)}
                              value={year}
                              onChange={setYear}
                              placeholder="Select Year"
                            />
                          </div>
                        </div>
                        <div className="px-3 py-1 bg-gray-100 rounded-full inline-block w-fit">
                          <span className="text-sm text-gray-600">
                            Basis: {quarter} {year} (YoY)
                          </span>
                        </div>
                      </div>
                    )}
                </div>
                <div className="flex flex-col gap-4 flex-1">
                  <div className="flex flex-row gap-4 items-center">
                    <h3 className="text-lg font-bold">Growth Multiplier</h3>
                    <InformationCircleIcon className="h-5 w-5 text-primary" data-tip={true} data-for="growth_multiplier_tooltip" />
                    <ReactTooltip id="growth_multiplier_tooltip" html={true} effect="solid" place="right">
                      Applies to demand velocity for sellout projection (forecast growth).
                    </ReactTooltip>
                  </div>
                  
                  {/* Percentage Input with +/- Buttons */}
                  <div className="flex flex-row items-center gap-2 min-h-[44px]">
                    <button
                      type="button"
                      onClick={() => {
                        const newValue = Math.max(0, growthMultiplier - 5);
                        if (newValue === 0) {
                          setSelectedPreset('0');
                        } else if (newValue === 10) {
                          setSelectedPreset('10');
                        } else if (newValue === 25) {
                          setSelectedPreset('25');
                        } else if (newValue === 50) {
                          setSelectedPreset('50');
                        } else {
                          setSelectedPreset('');
                        }
                        setGrowthMultiplier(newValue);
                      }}
                      className="h-10 w-10 rounded-md border border-gray-300 bg-white hover:bg-gray-300 flex items-center justify-center transition-colors"
                    >
                      <MinusIcon className="h-5 w-5 text-gray-700" />
                    </button>
                    <div className="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 bg-white flex-1 justify-between">
                      <div />
                      <input
                        type="number"
                        value={growthMultiplier}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '') {
                            setGrowthMultiplier(0);
                            setSelectedPreset('0');
                            return;
                          }
                          const numericValue = parseInt(inputValue, 10);
                          if (isNaN(numericValue) || numericValue < 0) {
                            return;
                          }
                          setGrowthMultiplier(numericValue);
                          if (numericValue === 0) {
                            setSelectedPreset('0');
                          } else if (numericValue === 10) {
                            setSelectedPreset('10');
                          } else if (numericValue === 25) {
                            setSelectedPreset('25');
                          } else if (numericValue === 50) {
                            setSelectedPreset('50');
                          } else {
                            setSelectedPreset('');
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === '+' || e.key === 'E') {
                            e.preventDefault();
                          }
                          if (growthMultiplier === 0 && /[0-9]/.test(e.key)) {
                            e.target.select();
                          }
                        }}
                        onFocus={(e) => {
                          if (growthMultiplier === 0) {
                            e.target.select();
                          }
                        }}
                        className="flex-1 text-center text-sm font-medium focus:outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                        min="0"
                      />
                      <span className="text-sm font-medium text-gray-700">%</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newValue = growthMultiplier + 5;
                        if (newValue === 0) {
                          setSelectedPreset('0');
                        } else if (newValue === 10) {
                          setSelectedPreset('10');
                        } else if (newValue === 25) {
                          setSelectedPreset('25');
                        } else if (newValue === 50) {
                          setSelectedPreset('50');
                        } else {
                          setSelectedPreset('');
                        }
                        setGrowthMultiplier(newValue);
                      }}
                      className="h-10 w-10 rounded-md border border-gray-300 bg-white hover:bg-gray-300 flex items-center justify-center transition-colors"
                    >
                      <PlusIcon className="h-5 w-5 text-gray-700" />
                    </button>
                  </div>
                  
                  {/* Quick Select Presets */}
                  <div className="flex flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setGrowthMultiplier(0);
                        setSelectedPreset('0');
                      }}
                      className={`px-4 py-2 min-w-[75px] focus:outline-none rounded-lg border text-sm font-medium transition-colors ${
                        selectedPreset === '0'
                          ? 'bg-primary text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      0%
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGrowthMultiplier(10);
                        setSelectedPreset('10');
                      }}
                      className={`px-4 py-2 min-w-[75px] focus:outline-none rounded-lg border text-sm font-medium transition-colors ${
                        selectedPreset === '10'
                          ? 'bg-primary text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      +10%
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGrowthMultiplier(25);
                        setSelectedPreset('25');
                      }}
                      className={`px-4 py-2 min-w-[75px] focus:outline-none rounded-lg border text-sm font-medium transition-colors ${
                        selectedPreset === '25'
                          ? 'bg-primary text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      +25%
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGrowthMultiplier(50);
                        setSelectedPreset('50');
                      }}
                      className={`px-4 py-2 min-w-[75px] focus:outline-none rounded-lg border text-sm font-medium transition-colors ${
                        selectedPreset === '50'
                          ? 'bg-primary text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      +50%
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500">
                    Applies to demand velocity for sellout projection (forecast growth).
                  </p>
                  
                  {/* <div className="px-3 py-1 bg-gray-100 rounded-full inline-block w-fit">
                    <span className="text-sm text-gray-600">
                      {growthMultiplier > 0 
                        ? `Growth Applied: +${growthMultiplier}% (sales velocity increased)`
                        : 'Adjusted Daily Avg: N/A'}
                    </span>
                  </div> */}
                </div>
              </section>
            </div>
            <div className="p-6 bg-white rounded-lg shadow-md my-4 border border-gray-200 hidden md:block">

              {/* Sticky header overlay for window scroll (keeps first column sticky + syncs horizontal scroll) */}
              <div
                ref={stickyHeaderScrollRef}
                className="z-30 bg-white border-b border-gray-200 overflow-x-auto no-scrollbar"
                style={{
                  display: showStickyHeader ? 'block' : 'none',
                  position: 'fixed',
                  top: stickyHeaderStyle.top,
                  left: stickyHeaderStyle.left,
                  width: stickyHeaderStyle.width,
                }}
              >
                <table
                  className="w-full min-w-full relative first-column-action border-collapse"
                  style={stickyHeaderTableMinWidth ? { minWidth: `${stickyHeaderTableMinWidth}px` } : undefined}
                >
                  <thead className="bg-white z-10 relative">
                    {renderHeaderContent({ disableTooltips: false, applyWidths: true, tooltipIdSuffix: 'sticky' })}
                  </thead>
                </table>
              </div>

              <div
                className="overflow-x-auto w-full"
                ref={tableContainerRef}
              >
                <div
                  className="overflow-y-auto"
                  ref={tableWrapperRef}
                >
                  <table className="w-full min-w-full relative first-column-action border-collapse">
                    <thead ref={tableHeaderRef} className="bg-white z-10 relative">
                      {renderHeaderContent({ disableTooltips: false, applyWidths: false })}
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {currentSales.map((product, index) => {
                        return (
                          <tr key={`${product.sku}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {filteredTableSettings.map(column => {
                              switch (column.value) {
                                case 'product_line':
                                  return (
                                    <td
                                      key={column.value}
                                      className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6 w-[320px] min-w-[320px] max-w-[320px]"
                                      style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb', backgroundImage: 'none' }}
                                    >
                                      <div className='flex flex-row gap-4 items-center justify-between'>
                                        <div className="flex flex-col min-w-0">
                                          <div className='flex items-center gap-2'>
                                            <div className="font-bold text-gray-900 max-w-52 whitespace-nowrap text-ellipsis overflow-hidden" title={product.productLine || "N/A"}>
                                              {product.productLine || "N/A"}
                                            </div>
                                            {product.isNew && (
                                              <div>
                                                <InformationCircleIcon className="h-5 w-5 text-primary" />
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-gray-500 truncate" title={getCategoryAndColor(product)}>
                                            {getCategoryAndColor(product)}
                                          </div>
                                          <div className="text-gray-500 truncate" title={product.sku}>
                                            {product.sku}
                                          </div>
                                        </div>
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryStatusColor(product.status)}`}>
                                          {product.status ? enumInventoryPhasedStatus[product.status] : enumInventoryPhasedStatus[ACTIVE]}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                case 'status':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm w-16 text-center align-middle">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryStatusColor(product.status)}`}>
                                        {product.status ? enumInventoryPhasedStatus[product.status] : enumInventoryPhasedStatus[ACTIVE]}
                                      </span>
                                    </td>
                                  );
                                case 'on_hand':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-20 text-center align-middle">{product.onHand}</td>
                                  );
                                case 'on_order':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-20 text-center align-middle">
                                      <div className="flex items-center justify-center gap-2">
                                        <span>{calculateRelatedByAdditionalOrder(product)?.onOrder || product.onOrder}</span>
                                        {getOnOrderDetailsTooltip(product.onOrderDetails, product.sku)}
                                      </div>
                                    </td>
                                  );
                                case 'extra_ordered':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-28 text-center align-middle">
                                      <div className="flex justify-center">
                                        <input
                                          type="number"
                                          value={
                                            editedExtraOrders[product.id]?.additionalOrder !== undefined
                                              ? editedExtraOrders[product.id]?.additionalOrder
                                              : 0
                                          }
                                          onKeyDown={(event) => {
                                            if (event.key === "-" || event.key === "e" || event.key === "E" || event.key === "+") {
                                              event.preventDefault();
                                            }
                                            const currentValue = editedExtraOrders[product.id]?.additionalOrder ?? 0;
                                            if (currentValue === 0 && /[0-9]/.test(event.key)) {
                                              event.target.select();
                                            }
                                          }}
                                          onFocus={(e) => {
                                            const currentValue = editedExtraOrders[product.id]?.additionalOrder ?? 0;
                                            if (currentValue === 0) {
                                              e.target.select();
                                            }
                                          }}
                                          onChange={(e) => {
                                            const inputValue = e.target.value;
                                            if (inputValue === '') {
                                              setEditedExtraOrders({
                                                ...editedExtraOrders,
                                                [product.id]: {
                                                  additionalOrder: 0
                                                }
                                              });
                                              return;
                                            }
                                            const numericValue = parseInt(inputValue, 10);
                                            if (isNaN(numericValue) || numericValue < 0) {
                                              return;
                                            }
                                            setEditedExtraOrders({
                                              ...editedExtraOrders,
                                              [product.id]: {
                                                additionalOrder: numericValue
                                              }
                                            });
                                          }}
                                          className="w-20 py-0.5 px-2 shadow-sm border border-gray-300 rounded-md shadow-sm text-sm"
                                        />
                                      </div>
                                    </td>
                                  );
                                case 'sales_last_30':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-12 text-center align-middle">
                                      <div className="flex items-center justify-center gap-2">
                                        <span>{calculateGrowthAdjustedValues(product)?.adjustedSalesLast30 ?? product.salesLast30}</span>
                                        {arrayHasItems(product.partsOfKit?.days_30) && (
                                          <>
                                            {getPartsOfKitTooltip(product.partsOfKit?.days_30, `parts_of_kit_tooltip_days_30_${product.id}`)}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  );
                                case 'sales_last_90':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-12 text-center align-middle">
                                      <div className="flex items-center justify-center gap-2">
                                        <span>{calculateGrowthAdjustedValues(product)?.adjustedSalesLast90 ?? product.salesLast90}</span>
                                        {arrayHasItems(product.partsOfKit?.days_90) && (
                                          <>
                                            {getPartsOfKitTooltip(product.partsOfKit?.days_90, `parts_of_kit_tooltip_days_90_${product.id}`)}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  );
                                case 'sales_last_365':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-12 text-center align-middle">
                                      <div className="flex items-center justify-center gap-2">
                                        <span>{calculateGrowthAdjustedValues(product)?.adjustedSalesLast365 ?? product.salesLast365}</span>
                                        {arrayHasItems(product.partsOfKit?.days_365) && (
                                          <>
                                            {getPartsOfKitTooltip(product.partsOfKit?.days_365, `parts_of_kit_tooltip_days_365_${product.id}`)}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  );
                                case 'sales_daily_avg':
                                  const growthAdjusted = calculateGrowthAdjustedValues(product);
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-20 text-center align-middle">
                                      {growthAdjusted?.adjustedSalesDailyAVG || product.salesDailyAVG}
                                    </td>
                                  );
                                case 'fba_sellable':
                                case 'fba_fc_transfer':
                                case 'fba_inbound': {
                                  const fbaKey = { fba_sellable: 'fbaSellable', fba_fc_transfer: 'fbaFcTransfer', fba_inbound: 'fbaInbound' }[column.value];
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-20 text-center align-middle">
                                      {product.fba ? product.fba[fbaKey] : '-'}
                                    </td>
                                  );
                                }
                                case 'fba_days':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm w-28 text-center align-middle">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getFbaDaysColor(product.fba)}`}>
                                        {product.fba?.fbaDays ?? 'N/A'}
                                      </span>
                                    </td>
                                  );
                                case 'fba_suggested_send': {
                                  const fba = product.fba;
                                  const sendValue = fba ? fbaSendFor(product) : null;
                                  const sendLocked = sendValue === null;
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 w-32 text-center align-middle">
                                      <div className="flex flex-col items-center gap-1">
                                        <input
                                          type="number"
                                          min="0"
                                          disabled={sendLocked || !fbaAccess.canDraft}
                                          value={sendLocked ? '' : sendValue}
                                          onKeyDown={(event) => {
                                            if (event.key === "-" || event.key === "e" || event.key === "E" || event.key === "+") event.preventDefault();
                                          }}
                                          onFocus={(event) => event.target.select()}
                                          onChange={(event) => {
                                            const numericValue = event.target.value === '' ? 0 : parseInt(event.target.value, 10);
                                            if (isNaN(numericValue) || numericValue < 0) return;
                                            setFbaSends({ ...fbaSends, [product.id]: numericValue });
                                            setFbaDraft(null);
                                          }}
                                          className="w-20 rounded-md border border-gray-300 px-2 py-1 text-center text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                          data-testid={`fba-send-${product.sku}`}
                                        />
                                        {fba?.fbaBlockedReason && <span className="text-[11px] text-gray-500">{fba.fbaBlockedReason}</span>}
                                        {!sendLocked && fba.fbaLimitedByVegas && <span className="text-[11px] text-yellow-700">Capped by Vegas floor</span>}
                                      </div>
                                    </td>
                                  );
                                }
                                case 'warehouse_days_of_stock':
                                  const growthAdjustedWarehouse = calculateGrowthAdjustedValues(product);
                                  const warehouseDays = growthAdjustedWarehouse?.warehouseDaysOfStock ?? product.warehouseDaysOfStock;
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm w-28 text-center align-middle">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryLeftColor(warehouseDays)}`}>
                                        {Math.round(warehouseDays || 0)}
                                      </span>
                                    </td>
                                  );
                                case 'days_of_stock':
                                  const growthAdjustedDays = calculateGrowthAdjustedValues(product);
                                  const daysOfStock = growthAdjustedDays?.daysOfStock ?? 
                                    (calculateRelatedByAdditionalOrder(product)?.daysOfStock ?? product.daysOfStock);
                                  return (
                                    <td key={column.value} className="whitespace-nowrap text-center px-3 py-2 text-sm w-28">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryLeftColor(daysOfStock)}`}>
                                        {Math.round(daysOfStock || 0)}
                                      </span>
                                    </td>
                                  );
                                  
                                case 'warehouse_sell_out_date':
                                  const growthAdjustedWarehouseSellOut = calculateGrowthAdjustedValues(product);
                                  const warehouseSellOut = growthAdjustedWarehouseSellOut?.warehouseSellOutDate ?? product.warehouseSellOutDays;
                                  const warehouseDaysForColor = growthAdjustedWarehouseSellOut?.warehouseDaysOfStock ?? product.warehouseDaysOfStock;
                                  return (
                                    <td key={column.value} className="whitespace-nowrap text-center px-3 py-2 text-sm text-gray-500 w-32">
                                      <span className={`px-2 text-center inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryLeftColor(warehouseDaysForColor || 0)}`}>
                                        {warehouseSellOut || ""}
                                      </span>
                                    </td>
                                  );
                                case 'sell_out_date':
                                  const growthAdjustedSellOut = calculateGrowthAdjustedValues(product);
                                  const sellOutDate = growthAdjustedSellOut?.sellOutDate ?? 
                                    (calculateRelatedByAdditionalOrder(product)?.sellOutDate ?? 
                                    (product.sellOutDate ? format(new Date(product.sellOutDate), 'MMM d, yyyy') : null));
                                  const daysForSellOutColor = growthAdjustedSellOut?.daysOfStock ?? 
                                    (calculateRelatedByAdditionalOrder(product)?.daysOfStock ?? product.daysOfStock);
                                  return (
                                    <td key={column.value} className="whitespace-nowrap text-center px-3 py-2 text-sm text-gray-500 w-32">
                                      <span className={`px-2 text-center inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryLeftColor(daysForSellOutColor || 0)}`}>
                                        {sellOutDate || ''}
                                      </span>
                                    </td>
                                  );
                                case 'reorder_deadline':
                                  const growthAdjustedReorder = calculateGrowthAdjustedValues(product);
                                  const reorderDeadline = growthAdjustedReorder?.reorderDeadline ?? 
                                    (calculateRelatedByAdditionalOrder(product)?.reorderDeadline ?? product.reorderDeadline);
                                  const daysForReorderColor = growthAdjustedReorder?.daysOfStock ?? 
                                    (calculateRelatedByAdditionalOrder(product)?.daysOfStock ?? product.daysOfStock);
                                  return (
                                    <td key={column.value} className="whitespace-nowrap text-center px-3 py-2 text-sm text-gray-500 w-24">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${reorderDeadline ? getInventoryLeftColor(daysForReorderColor) : "bg-red-100 text-red-800"}`}>
                                        {reorderDeadline || 'ASAP'}
                                      </span>
                                    </td>
                                  );
                                case 'reorder_quantity':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm text-center text-gray-500 w-24">{product.reorderQuantity || "0"}</td>
                                  );
                                case 'stock_arrives_on':
                                  return (
                                    <td key={column.value} className="whitespace-nowrap text-center px-3 py-2 text-sm text-gray-500 w-24">
                                      {getArrivesOnResult(product.arrivesOn, product.sku, product.productLine, product.arrivesOnValue, product.productLeadTime)}
                                    </td>
                                  );
                                case 'sell_out_180_days':
                                  const growthAdjusted180 = calculateGrowthAdjustedValues(product);
                                  const sellOut180Days = growthAdjusted180?.sellOut180Days ?? 
                                    (calculateRelatedByAdditionalOrder(product)?.sellOut180Days ?? product.sellOut180Days);
                                  return (
                                    <td key={column.value} className="whitespace-nowrap px-3 py-2 text-sm w-32 align-middle">
                                      <div className="flex items-center justify-center">
                                        {sellOut180Days || sellOut180Days === null ? (
                                          <XCircleIcon className="h-5 w-5 text-red-500" />
                                        ) : (
                                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                        )}
                                      </div>
                                    </td>
                                  );
                                default:
                                  return null;
                              }
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {!currentIsFirstLoadComplete && tableIsLoading() && (
              <div className="z-10 h-60">
                <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' testId={testId ? `loader-table-${testId}` : undefined} />
              </div>
            )}
          </div>
          {currentIsFirstLoadComplete && (
            <div className="mt-6 mb-4">
              <MobilePaginationBar
                currentItemsPerPage={currentItemsPerPage}
                setCurrentItemsPerPage={(itemsPerPage) => {
                  setCurrentItemsPerPage(itemsPerPage);
                  scrollToTableTop();
                }}
                currentPage={currentPage}
                setCurrentPage={(page) => {
                  setCurrentPage(page);
                  scrollToTableTop();
                }}
                rangeRows={pagination.rangeRows}
                isTheLastPage={pagination.isTheLastPage}
              />
              {isFba && (
                <div className="hidden md:flex items-center justify-end gap-4 mt-4" data-testid="amazon-fba-draft-action">
                  {fbaDraft && (
                    <span className={`text-sm ${fbaDraft.error ? 'text-red-700' : 'text-gray-700'}`}>
                      {fbaDraft.text}
                      {fbaDraft.number && (
                        <button type="button" className="ml-2 underline text-primary" onClick={() => navigate(ROUTES.FBA_TRANSFERS)}>Open Transfers</button>
                      )}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={createFbaDraft}
                    disabled={!fbaAccess.canDraft || !fbaDraftLines.length || fbaDraft?.busy || amazonFba?.sync?.state !== 'FRESH'}
                    title={fbaAccess.canDraft ? 'Creates a Draft in Transfers. Nothing is removed from ShipHero until it is published there.' : 'Needs Edit on Stock Levels: Amazon FBA and FBA Transfers'}
                    className="px-4 py-2 rounded-md bg-black text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Create FBA transfer draft{fbaDraftLines.length ? ` (${fbaDraftLines.length} SKU${fbaDraftLines.length === 1 ? "" : "s"}, ${fbaDraftUnits} units)` : ''}
                  </button>
                </div>
              )}
              <div className="hidden md:block">
              <Pagination
                currentItemsPerPage={currentItemsPerPage}
                rangeRows={pagination.rangeRows}
                currentPage={currentPage}
                setCurrentPage={(page) => {
                  setCurrentPage(page);
                  scrollToTableTop();
                }}
                isTheLastPage={pagination.isTheLastPage}
                totalPages={pagination.totalPages}
                setCurrentItemsPerPage={(itemsPerPage) => {
                  setCurrentItemsPerPage(itemsPerPage);
                  scrollToTableTop();
                }}
                testId={testId}
              />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryByShopifyAndAmazonCalculation; 